import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import EnquiryModal from "@/components/EnquiryModal";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { isListingVisible, SHOW_RENT } from "@/lib/featureFlags";
import { PropertyMap } from "@/components/PropertyMap";
import { LayoutGrid, Map as MapIcon } from "lucide-react";

const ViewToggle = ({ view, setView }) => (
  <div className="inline-flex p-1 rounded-full bg-[#F4F7FB] border border-[#EBEFF5]" data-testid="view-toggle">
    {[
      ["grid", LayoutGrid, "Grid"],
      ["map", MapIcon, "Map"],
    ].map(([key, Icon, label]) => (
      <button
        key={key}
        onClick={() => setView(key)}
        data-testid={`view-toggle-${key}`}
        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
          view === key ? "bg-[#0B2545] text-[#C9A84C]" : "text-[#1F2A44] hover:text-[#0B2545]"
        }`}
      >
        <Icon size={14} /> {label}
      </button>
    ))}
  </div>
);

export default function Properties() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquiryFor, setEnquiryFor] = useState(null);
  const [view, setView] = useState("grid");

  const filters = useMemo(() => {
    const q = Object.fromEntries(searchParams.entries());
    const out = {};
    if (q.city) out.city = q.city;
    if (q.property_type) out.property_type = q.property_type;
    if (q.listing_type && (SHOW_RENT || q.listing_type !== "rent")) out.listing_type = q.listing_type;
    if (q.min_price) out.min_price = Number(q.min_price);
    if (q.max_price) out.max_price = Number(q.max_price);
    if (q.exclusive === "1") out.exclusive = true;
    if (q.assured === "1") out.assured = true;
    return out;
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = { ...filters };
        delete params.assured;
        const { data } = await api.get("/properties", { params });
        const visible = data.filter(isListingVisible);
        const filtered = filters.assured ? visible.filter((p) => p.is_assured_return) : visible;
        setProperties(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]);

  return (
    <div className="min-h-screen bg-white text-[#0B2545] font-sans">
      <Navbar />

      <section className="bg-[#F4F7FB] border-b border-[#EBEFF5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8 md:pt-14 md:pb-10">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
            {t.nav.properties}
          </div>
          <h1 className="mt-2 font-serif text-3xl md:text-5xl text-[#0B2545] rc-heading">
            {t.sections.all_listings}
          </h1>
          <p className="mt-3 text-[#1F2A44] max-w-xl">{t.sections.all_sub}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 md:-mt-6 relative z-10">
        <SearchBar compact defaults={filters} />
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-[#1F2A44]" data-testid="properties-count">
            {loading ? "…" : `${properties.length} properties`}
          </span>
          <ViewToggle view={view} setView={setView} />
        </div>

        {view === "map" && !loading && (
          <div className="mt-6">
            <PropertyMap properties={properties} />
          </div>
        )}

        <div className={`mt-6 grid ${view === "map" ? "hidden" : ""} grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8`}>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-[#F4F7FB] animate-pulse h-[380px]"
                />
              ))
            : properties.map((p) => (
                <PropertyCard key={p.id} property={p} onEnquire={setEnquiryFor} />
              ))}
          {!loading && properties.length === 0 && (
            <p className="col-span-full text-[#1F2A44]" data-testid="no-results">
              {t.sections.no_results}
            </p>
          )}
        </div>
      </section>

      <Footer />
      <EnquiryModal
        property={enquiryFor}
        open={!!enquiryFor}
        onClose={() => setEnquiryFor(null)}
      />
    </div>
  );
}
